import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  OpenedUserListItem,
  AddToOpenedUserList,
  resetOpenedUserList,
  resetOpenedUserListSearch,
  DEFAULT_OPEN_LIST_TYPE,
} from '$logic/opened-user-list'
import { RootState } from '$setup/redux'

function useResetOnUnmount(key = DEFAULT_OPEN_LIST_TYPE) {
  useEffect(() => {
    // Reset list when unmounted
    return () => resetOpenedUserList(key)
  }, [key])
}

function useResetSearchOnUnmount(key = DEFAULT_OPEN_LIST_TYPE) {
  useEffect(() => {
    // Reset list when unmounted
    return () => resetOpenedUserListSearch(key)
  }, [key])
}

export function useOpenedUserListItems(
  key = DEFAULT_OPEN_LIST_TYPE,
): OpenedUserListItem[] {
  useResetOnUnmount(key)

  const normalizedItems = useSelector(
    (state: RootState) => state.main.openedUserList[key].users,
  )
  return Object.values(normalizedItems)
}

export function useOpenedUserListSearchedItems(
  key = DEFAULT_OPEN_LIST_TYPE,
): OpenedUserListItem[] {
  useResetSearchOnUnmount(key)

  const storage = useSelector(
    (state: RootState) => state.main.openedUserList[key],
  )
  return Object.keys(storage.searchedIds).map(id => storage.users[id])
}

export function useOpenedUserListAsyncIntegration<T extends OpenedUserListItem>(
  fetchItems: () => Promise<T[]>,
  deps: unknown[] = [],
  key = DEFAULT_OPEN_LIST_TYPE,
) {
  useResetOnUnmount(key)

  const [loading, setLoading] = useState(true)
  const normalizedItems = useSelector(
    (state: RootState) => state.main.openedUserList[key]?.users,
  ) as T[]

  useEffect(
    () => {
      resetOpenedUserList()
      setLoading(true)

      fetchItems()
        .then(items => AddToOpenedUserList(items, key))
        .finally(() => setLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...deps],
  )

  const items = useMemo(() => {
    return Object.values(normalizedItems || [])
  }, [normalizedItems])

  return { loading, items }
}
